import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Model, SurveyModel } from 'survey-core';
import { Survey } from 'survey-react-ui';
import 'survey-core/survey-core.css';
import surveyJson from './surveyModel.json';
import { getCookie } from './api/client';
import {
  completeSurvey,
  getSessionStatus,
  startSession,
  submitSurveyStep,
  subscribeEmail,
} from './api/surveyApi';
import {
  buildQuestionCodeMap,
  buildQuestionMetadataMap,
  toQuestionCodeAnswers,
} from './api/questionCodeMapper';
import type {
  AppPhase,
  SubmitStatus,
  SavedSurveyState,
  ClientContext,
} from './types/api';
import WelcomeBlock from './components/WelcomeBlock';
import HeaderComponent from './components/HeaderComponent';
import ThankYouBlock from './components/ThankYouBlock';
import FooterComponent from './components/FooterComponent';
import LegalsPage from './components/LegalsPage';
import AboutUsPage from './components/AboutUsPage';
import StatusAlert from './components/StatusAlert';
import type { AlertSeverity } from './components/StatusAlert';
import { countries } from './data/countries';

type Language = 'en' | 'es';

interface SurveyTranslations {
  pageNextText: string;
  pagePrevText: string;
  completeText: string;
}

interface StatusMessages {
  serviceUnavailable: string;
  compatibilityMode: string;
  sessionMissing: string;
  csrfInvalid: string;
  sessionConflict: string;
  sessionExpired: string;
  validationFailed: string;
  rateLimited: string;
  mappingMissing: string;
  completionPending: string;
  turnstileRequired: string;
  turnstileUnavailable: string;
  closeMessage: string;
}

const translations: Record<Language, SurveyTranslations> = {
  en: {
    pageNextText: 'Next',
    pagePrevText: 'Previous',
    completeText: 'Finish',
  },
  es: {
    pageNextText: 'Siguiente',
    pagePrevText: 'Anterior',
    completeText: 'Finalizar',
  },
};

const statusMessages: Record<Language, StatusMessages> = {
  en: {
    serviceUnavailable: 'We could not start the survey right now. Please try again shortly.',
    compatibilityMode: 'Real-time session is unavailable. Compatibility mode is enabled for local testing.',
    sessionMissing: 'Your session is no longer valid. Please start the survey again.',
    csrfInvalid: 'Your security verification could not be confirmed. Please try again.',
    sessionConflict: 'Your survey progress was updated. Please continue from the suggested step.',
    sessionExpired: 'Your session expired. Please start a new survey to continue.',
    validationFailed: 'Some information needs attention. Please review and try again.',
    rateLimited: 'Too many attempts in a short time. Please wait a moment and try again.',
    mappingMissing: 'This survey is temporarily unavailable. Please try again later.',
    completionPending: 'Finalizing your survey submission...',
    turnstileRequired: 'Please complete the security verification before starting the survey.',
    turnstileUnavailable: 'Security verification is currently unavailable. Please refresh and try again.',
    closeMessage: 'Close message',
  },
  es: {
    serviceUnavailable: 'No pudimos iniciar la encuesta en este momento. Inténtelo nuevamente en breve.',
    compatibilityMode: 'La sesión en tiempo real no está disponible. Se habilitó el modo de compatibilidad para pruebas locales.',
    sessionMissing: 'Su sesión ya no es válida. Inicie la encuesta nuevamente.',
    csrfInvalid: 'No pudimos confirmar la verificación de seguridad. Inténtelo nuevamente.',
    sessionConflict: 'El progreso de su encuesta fue actualizado. Continúe desde el paso sugerido.',
    sessionExpired: 'Su sesión expiró. Inicie una nueva encuesta para continuar.',
    validationFailed: 'Algunos datos requieren revisión. Verifíquelos e intente nuevamente.',
    rateLimited: 'Demasiados intentos en poco tiempo. Espere un momento e intente otra vez.',
    mappingMissing: 'Esta encuesta no está disponible temporalmente. Inténtelo más tarde.',
    completionPending: 'Finalizando el envio de tu encuesta...',
    turnstileRequired: 'Completa la verificacion de seguridad antes de iniciar la encuesta.',
    turnstileUnavailable: 'La verificacion de seguridad no esta disponible. Recarga la pagina e intenta nuevamente.',
    closeMessage: 'Cerrar mensaje',
  },
};

const STORAGE_KEY = 'survey_state';
const EMAIL_OPT_IN_SENT_KEY = 'survey_email_opt_in_sent';
const DEFAULT_FLOW_MODE = (import.meta.env.VITE_SURVEY_FLOW_MODE || 'session').toLowerCase();
const SESSION_MODE_ENABLED_BY_DEFAULT = DEFAULT_FLOW_MODE !== 'legacy';
const TURNSTILE_ENABLED = (import.meta.env.VITE_TURNSTILE_ENABLED || 'false').toLowerCase() === 'true';
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';
const TURNSTILE_CONFIGURED = TURNSTILE_ENABLED && Boolean(TURNSTILE_SITE_KEY);
const QUESTION_CODE_MAP = buildQuestionCodeMap(surveyJson as never);
const QUESTION_METADATA_MAP = buildQuestionMetadataMap(surveyJson as never);
const COUNTRY_CODE_SET = new Set(countries.map((country) => country.code));
const MOBILE_BREAKPOINT = 768;
const MOBILE_HEADER_COMPACT_SCROLL_Y = 20;

function getSavedState(): SavedSurveyState | null {
  const savedState = localStorage.getItem(STORAGE_KEY);

  if (!savedState) {
    return null;
  }

  try {
    return JSON.parse(savedState);
  } catch {
    return null;
  }
}

function hasSavedData(): boolean {
  const parsed = getSavedState();
  return Boolean(parsed?.data && Object.keys(parsed.data).length > 0);
}

function hasEmailOptInSent(): boolean {
  return localStorage.getItem(EMAIL_OPT_IN_SENT_KEY) === '1';
}

function setEmailOptInSent(sent: boolean): void {
  if (sent) {
    localStorage.setItem(EMAIL_OPT_IN_SENT_KEY, '1');
    return;
  }

  localStorage.removeItem(EMAIL_OPT_IN_SENT_KEY);
}

function isProductionMode(): boolean {
  return import.meta.env.MODE === 'production';
}

function mapApiErrorToMessage(status: number): keyof StatusMessages {
  switch (status) {
    case 401:
      return 'sessionMissing';
    case 403:
      return 'csrfInvalid';
    case 409:
      return 'sessionConflict';
    case 410:
      return 'sessionExpired';
    case 400:
      return 'turnstileRequired';
    case 422:
      return 'validationFailed';
    case 429:
      return 'rateLimited';
    default:
      return 'serviceUnavailable';
  }
}

function mapMessageSeverity(messageKey: keyof StatusMessages): AlertSeverity {
  switch (messageKey) {
    case 'completionPending':
      return 'info';
    case 'rateLimited':
    case 'validationFailed':
    case 'sessionConflict':
    case 'turnstileRequired':
      return 'warning';
    case 'compatibilityMode':
      return 'info';
    default:
      return 'error';
  }
}

function resolveExpectedStepIds(session: { expected_step_ids?: string[]; expected_steps?: Array<{ step_id: string }> }): string[] {
  if (Array.isArray(session?.expected_step_ids)) {
    return session.expected_step_ids;
  }

  if (Array.isArray(session?.expected_steps)) {
    return session.expected_steps.map((step) => step.step_id).filter(Boolean);
  }

  return [];
}

function resolveStepId(pageIndex: number, expectedStepIds: string[]): string {
  if (Array.isArray(expectedStepIds) && expectedStepIds[pageIndex]) {
    return expectedStepIds[pageIndex];
  }

  return `S${pageIndex + 1}`;
}

function getAnswersForPage(survey: SurveyModel, pageIndex: number): Record<string, unknown> {
  const page = survey.pages?.[pageIndex];

  if (!page) {
    return {};
  }

  const answers: Record<string, unknown> = {};

  const questions = Array.isArray(page.questions) ? page.questions : [];

  questions.forEach((question) => {
    if (Object.prototype.hasOwnProperty.call(survey.data, question.name)) {
      answers[question.name] = survey.data[question.name];
    }

    const commentFieldName = `${question.name}-Comment`;
    if (Object.prototype.hasOwnProperty.call(survey.data, commentFieldName)) {
      answers[commentFieldName] = survey.data[commentFieldName];
    }
  });

  return answers;
}

function normalizeCountryCode(value: string | null | undefined): string {
  const normalized = (value || '').trim().toUpperCase();

  if (!/^[A-Z]{2}$/.test(normalized)) {
    return '';
  }

  if (!COUNTRY_CODE_SET.has(normalized)) {
    return '';
  }

  return normalized;
}

function getInitialCountry(): string {
  const savedState = getSavedState();
  const savedCountry = normalizeCountryCode(savedState?.country);

  if (savedCountry) {
    return savedCountry;
  }

  return '';
}

function normalizeLocaleCandidate(value: string | null | undefined): string {
  const candidate = (value || '').trim();

  if (!candidate) {
    return '';
  }

  try {
    const [canonicalLocale] = Intl.getCanonicalLocales(candidate);
    return canonicalLocale || '';
  } catch {
    return '';
  }
}

function getCanonicalBrowserLocale(): string {
  const localeCandidates = typeof navigator === 'undefined'
    ? []
    : [
      ...(Array.isArray(navigator.languages) ? navigator.languages : []),
      navigator.language,
    ];

  for (const candidate of localeCandidates) {
    const canonicalLocale = normalizeLocaleCandidate(candidate);
    if (canonicalLocale) {
      return canonicalLocale;
    }
  }

  return '';
}

function getBrowserTimeZone(): string {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof timeZone === 'string' ? timeZone.trim() : '';
  } catch {
    return '';
  }
}

function getClientContext(country: string): ClientContext {
  return {
    locale: getCanonicalBrowserLocale(),
    country,
    tz: getBrowserTimeZone(),
  };
}

function isMobileViewportWidth(): boolean {
  return window.innerWidth <= MOBILE_BREAKPOINT;
}

function scrollViewportToTop(): void {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: 'auto',
  });
}

function SurveyComponent(): React.ReactElement | null {
  const [currentLocale, setCurrentLocale] = useState<Language>('en');
  const [selectedCountry, setSelectedCountry] = useState<string>(() => getInitialCountry());
  const [model, setModel] = useState<SurveyModel | null>(null);
  const [useSessionApi, setUseSessionApi] = useState(SESSION_MODE_ENABLED_BY_DEFAULT);
  const [showSurvey, setShowSurvey] = useState<boolean>(() => hasSavedData());
  const [showThankYou, setShowThankYou] = useState(false);
  const [showLegals, setShowLegals] = useState(false);
  const [showAboutUs, setShowAboutUs] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>(null);
  const [emailAlreadySent, setEmailAlreadySent] = useState<boolean>(() => hasEmailOptInSent());
  const [progress, setProgress] = useState<number>(() => {
    const parsed = getSavedState();

    if (typeof parsed?.currentPageNo === 'number' && parsed.currentPageNo > 0) {
      const totalPages = surveyJson.pages?.length || 7;
      return (parsed.currentPageNo / totalPages) * 100;
    }

    return 0;
  });
  const [appPhase, setAppPhase] = useState<AppPhase>('boot');
  const [flowMessageKey, setFlowMessageKey] = useState<keyof StatusMessages | null>(null);
  const [isFlowMessageDismissed, setIsFlowMessageDismissed] = useState(false);
  const [expectedStepIds, setExpectedStepIds] = useState<string[]>([]);
  const [receivedStepIds, setReceivedStepIds] = useState<string[]>([]);
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const [turnstileResetNonce, setTurnstileResetNonce] = useState<number>(0);
  const [isMobileViewport, setIsMobileViewport] = useState<boolean>(() => isMobileViewportWidth());
  const [isHeaderCompact, setIsHeaderCompact] = useState<boolean>(() => window.scrollY > MOBILE_HEADER_COMPACT_SCROLL_Y);
  const [mobilePaddingTop, setMobilePaddingTop] = useState<string>('0px');
  const [headerPosition, setHeaderPosition] = useState<React.CSSProperties['position']>('sticky');

  const surveyRef = useRef<Survey>(null);
  const modelRef = useRef<SurveyModel | null>(null);
  const previousPageRef = useRef(0);
  const useSessionApiRef = useRef(useSessionApi);
  const expectedStepIdsRef = useRef(expectedStepIds);
  const receivedStepIdsRef = useRef(receivedStepIds);
  const csrfRetryTrackerRef = useRef<Record<string, number>>({});

  useEffect(() => {
    useSessionApiRef.current = useSessionApi;
  }, [useSessionApi]);

  useEffect(() => {
    expectedStepIdsRef.current = expectedStepIds;
  }, [expectedStepIds]);

  useEffect(() => {
    receivedStepIdsRef.current = receivedStepIds;
  }, [receivedStepIds]);

  useEffect(() => {
    modelRef.current = model;
  }, [model]);

  useEffect(() => {
    const handleResize = (): void => {
      const isMobile = isMobileViewportWidth();
      setIsMobileViewport(isMobile);

      if (!isMobile) {
        setIsHeaderCompact(false);
        return;
      }

      setIsHeaderCompact(window.scrollY > MOBILE_HEADER_COMPACT_SCROLL_Y);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!isMobileViewport) {
      setIsHeaderCompact(false);
      return;
    }

    const handleScroll = (): void => {
      setIsHeaderCompact(window.scrollY > MOBILE_HEADER_COMPACT_SCROLL_Y);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMobileViewport]);

  const setFlowMessage = useCallback((messageKey: keyof StatusMessages | null): void => {
    setFlowMessageKey(messageKey);
    setIsFlowMessageDismissed(false);
  }, []);

  const saveSurveyState = (survey: SurveyModel): void => {
    const state: SavedSurveyState = {
      data: survey.data as Record<string, unknown>,
      currentPageNo: survey.currentPageNo,
      country: selectedCountry || undefined,
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  };

  const loadSurveyState = (survey: SurveyModel): void => {
    const parsed = getSavedState();

    if (!parsed) {
      return;
    }

    if (parsed.data) {
      survey.data = parsed.data;
    }

    if (typeof parsed.currentPageNo === 'number' && parsed.currentPageNo > 0) {
      survey.currentPageNo = parsed.currentPageNo;
    }
  };

  const clearSurveyState = (): void => {
    localStorage.removeItem(STORAGE_KEY);
  };

  const clearEmailOptInSentFlag = (): void => {
    setEmailOptInSent(false);
    setEmailAlreadySent(false);
  };

  const updateProgressFromSurvey = (survey: SurveyModel): void => {
    if (survey.pageCount > 0) {
      const progressValue = (survey.currentPageNo / survey.pageCount) * 100;
      setProgress(progressValue);
    }
  };

  const applyApiErrorState = (status: number): void => {
    const messageKey = mapApiErrorToMessage(status);
    setFlowMessage(messageKey);

    if (status === 401 || status === 410) {
      setAppPhase('readyToStart');
      setShowSurvey(false);
      setShowThankYou(false);
    } else if (status === 429) {
      setAppPhase('rateLimited');
    } else {
      setAppPhase('blocked');
    }
  };

  const fallbackToLegacyMode = (): void => {
    setUseSessionApi(false);
    setAppPhase('legacy');
    setFlowMessage('compatibilityMode');
    setExpectedStepIds([]);
    setReceivedStepIds([]);

    if (model && hasSavedData()) {
      loadSurveyState(model);
      previousPageRef.current = model.currentPageNo;
      setShowSurvey(true);
      updateProgressFromSurvey(model);
    }
  };

  const handleBackendAvailabilityIssue = (): void => {
    if (!isProductionMode()) {
      fallbackToLegacyMode();
      return;
    }

    setAppPhase('blocked');
    setShowSurvey(false);
    setShowThankYou(false);
    setFlowMessage('serviceUnavailable');
  };

  const applySessionToUI = (session: { status?: string; is_completed?: boolean; expected_step_ids?: string[]; expected_steps?: Array<{ step_id: string }>; received_step_ids?: string[] }, survey: SurveyModel): void => {
    const expectedIds = resolveExpectedStepIds(session);
    const receivedIds = Array.isArray(session?.received_step_ids) ? session.received_step_ids : [];
    const status = session?.status;

    setExpectedStepIds(expectedIds);
    setReceivedStepIds(receivedIds);

    if (status === 'completed' || session?.is_completed) {
      setAppPhase('completed');
      setShowSurvey(true);
      setShowThankYou(true);
      setProgress(100);
      return;
    }

    if (status === 'expired') {
      setAppPhase('readyToStart');
      setFlowMessage('sessionExpired');
      setShowSurvey(false);
      setShowThankYou(false);
      return;
    }

    if (status === 'active') {
      const nextPageIndex = Math.min(receivedIds.length, Math.max(survey.pageCount - 1, 0));

      survey.currentPageNo = nextPageIndex;
      previousPageRef.current = nextPageIndex;
      updateProgressFromSurvey(survey);
      setAppPhase('inSurvey');
      setShowSurvey(true);
      setShowThankYou(false);
      return;
    }

    setAppPhase('readyToStart');
    setShowSurvey(false);
  };

  const getFirstMissingStepIndex = (session: { expected_step_ids?: string[]; expected_steps?: Array<{ step_id: string }>; received_step_ids?: string[] }, survey: SurveyModel): number => {
    const expectedIds = resolveExpectedStepIds(session);
    const receivedIds = Array.isArray(session?.received_step_ids) ? session.received_step_ids : [];
    const firstMissingStepId = expectedIds.find((stepId) => !receivedIds.includes(stepId));

    if (firstMissingStepId) {
      const expectedIndex = expectedIds.indexOf(firstMissingStepId);

      if (expectedIndex >= 0) {
        return expectedIndex;
      }
    }

    return Math.min(receivedIds.length, Math.max(survey.pageCount - 1, 0));
  };

  const recoverFromConflict = async (survey: SurveyModel): Promise<boolean> => {
    try {
      const statusData = await getSessionStatus();
      const session = statusData?.session || {};
      const missingIndex = getFirstMissingStepIndex(session, survey);

      applySessionToUI(session, survey);
      survey.currentPageNo = missingIndex;
      previousPageRef.current = missingIndex;
      updateProgressFromSurvey(survey);
      setShowSurvey(true);
      setShowThankYou(false);
      setAppPhase('inSurvey');
      setFlowMessage('sessionConflict');
      return true;
    } catch (recoveryError) {
      applyApiErrorState((recoveryError as { status?: number })?.status || 0);
      return false;
    }
  };

  const withCsrfRetry = async (
    actionKey: string,
    action: () => Promise<unknown>,
    surveyForRecovery: SurveyModel | null = modelRef.current
  ): Promise<unknown> => {
    try {
      const result = await action();
      csrfRetryTrackerRef.current[actionKey] = 0;
      return result;
    } catch (error) {
      const err = error as { status?: number };
      if (err?.status === 403) {
        const retries = csrfRetryTrackerRef.current[actionKey] || 0;

        if (retries < 1 && surveyForRecovery) {
          csrfRetryTrackerRef.current[actionKey] = retries + 1;

          try {
            const refreshData = await getSessionStatus();
            applySessionToUI(refreshData?.session || {}, surveyForRecovery);
            const replayResult = await action();
            csrfRetryTrackerRef.current[actionKey] = 0;
            return replayResult;
          } catch (retryError) {
            throw retryError;
          }
        }
      }

      throw error;
    }
  };

  const startBackendSession = async (survey: SurveyModel): Promise<void> => {
    setFlowMessage(null);
    setAppPhase('startingSession');

    const countryCode = normalizeCountryCode(selectedCountry);

    if (!countryCode) {
      setAppPhase('readyToStart');
      setFlowMessage('validationFailed');
      return;
    }

    if (TURNSTILE_ENABLED && !TURNSTILE_SITE_KEY) {
      setAppPhase('blocked');
      setFlowMessage('turnstileUnavailable');
      return;
    }

    if (TURNSTILE_ENABLED && !turnstileToken) {
      setAppPhase('readyToStart');
      setFlowMessage('turnstileRequired');
      return;
    }

    try {
      const payload = {
        turnstile_token: TURNSTILE_ENABLED ? turnstileToken : '',
        client: getClientContext(countryCode),
      };

      const data = await startSession(payload);
      applySessionToUI(data?.session || {}, survey);
      setTurnstileToken('');
    } catch (error) {
      const err = error as { status?: number };

      if (TURNSTILE_ENABLED && (err?.status === 400 || err?.status === 403)) {
        setTurnstileToken('');
        setTurnstileResetNonce((value) => value + 1);
        setAppPhase('readyToStart');
        setFlowMessage('turnstileRequired');
        return;
      }

      if (err?.status === 429 || err?.status === 422) {
        applyApiErrorState(err.status);
        return;
      }

      handleBackendAvailabilityIssue();
    }
  };

  const submitStepForIndex = async (survey: SurveyModel, pageIndex: number): Promise<boolean> => {
    if (!useSessionApiRef.current || pageIndex < 0) {
      return false;
    }

    const stepId = resolveStepId(pageIndex, expectedStepIdsRef.current);

    if (receivedStepIdsRef.current.includes(stepId)) {
      return true;
    }

    try {
      const rawAnswers = getAnswersForPage(survey, pageIndex);
      const qCodeAnswers = toQuestionCodeAnswers(
        rawAnswers,
        QUESTION_CODE_MAP,
        QUESTION_METADATA_MAP
      );

      const data = await withCsrfRetry(
        'submitStep',
        () =>
          submitSurveyStep({
            step_id: stepId,
            answers: qCodeAnswers,
            client_step_index: pageIndex + 1,
          }),
        survey
      );

      const session = (data as { session?: { expected_step_ids?: string[]; received_step_ids?: string[] } })?.session || {};

      if (Array.isArray(session.expected_step_ids)) {
        setExpectedStepIds(session.expected_step_ids);
      }

      if (Array.isArray(session.received_step_ids)) {
        setReceivedStepIds(session.received_step_ids);
      } else {
        setReceivedStepIds([...receivedStepIdsRef.current, stepId]);
      }

      setFlowMessage(null);
      return true;
    } catch (error) {
      const err = error as { code?: string; status?: number };
      if (err?.code === 'MISSING_Q_MAPPING' || err?.code === 'MISSING_Q_VALUE_MAPPING') {
        setAppPhase('blocked');
        setFlowMessage('mappingMissing');
        return false;
      }

      if (err?.status === 409) {
        await recoverFromConflict(survey);
        return false;
      }

      applyApiErrorState(err?.status || 0);
      return false;
    }
  };

  const handleCompleteWithApi = async (survey: SurveyModel): Promise<void> => {
    setIsFinalizing(true);
    setFlowMessage('completionPending');

    const lastPageIndex = Math.max(survey.pageCount - 1, 0);
    const canComplete = await submitStepForIndex(survey, lastPageIndex);

    if (!canComplete) {
      setIsFinalizing(false);
      return;
    }

    try {
      await withCsrfRetry('completeSurvey', () => completeSurvey({}), survey);

      setFlowMessage(null);
      setAppPhase('completed');
      setShowThankYou(true);
      setProgress(100);
      clearSurveyState();
    } catch (error) {
      const err = error as { status?: number };

      if (err?.status === 409) {
        await recoverFromConflict(survey);
        return;
      }

      applyApiErrorState(err?.status || 0);
    } finally {
      setIsFinalizing(false);
    }
  };

  useEffect(() => {
    const survey = new Model(surveyJson);
    survey.showQuestionNumbers = 'on';
    survey.questionStartIndex = '1';
    survey.showProgressBar = 'off';

    if (hasSavedData()) {
      loadSurveyState(survey);
    }

    previousPageRef.current = survey.currentPageNo;
    updateProgressFromSurvey(survey);

    survey.onCurrentPageChanged.add(() => {
      const previousPageNo = previousPageRef.current;
      const currentPageNo = survey.currentPageNo;

      updateProgressFromSurvey(survey);
      saveSurveyState(survey);

      if (useSessionApiRef.current) {
        if (currentPageNo > previousPageNo) {
          void submitStepForIndex(survey, previousPageNo);
        }
      }

      previousPageRef.current = currentPageNo;
    });

    survey.onComplete.add(() => {
      if (useSessionApiRef.current) {
        void handleCompleteWithApi(survey);
        return;
      }

      clearSurveyState();
      setShowThankYou(true);
      setProgress(100);
    });

    survey.onValueChanged.add(() => {
      saveSurveyState(survey);
    });

    setModel(survey);
    modelRef.current = survey;
  }, []);

  useEffect(() => {
    if (!model) {
      return;
    }

    if (hasSavedData()) {
      setShowSurvey(true);
      if (useSessionApi) {
        setAppPhase('inSurvey');
      }
      return;
    }

    if (!useSessionApi) {
      return;
    }

    let isActive = true;

    const bootstrap = async (): Promise<void> => {
      setAppPhase('checkingSession');
      setFlowMessage(null);

      if (!getCookie('csrf')) {
        if (isActive) {
          setAppPhase('readyToStart');
          setShowSurvey(false);
        }
        return;
      }

      try {
        const data = await getSessionStatus();

        if (!isActive) {
          return;
        }

        applySessionToUI(data?.session || {}, model);
      } catch (error) {
        const err = error as { status?: number };
        if (!isActive) {
          return;
        }

        if (err?.status === 401 || err?.status === 403) {
          setAppPhase('readyToStart');
          setShowSurvey(false);
          return;
        }

        if (err?.status === 410 || err?.status === 429 || err?.status === 422) {
          applyApiErrorState(err.status);
          return;
        }

        handleBackendAvailabilityIssue();
      }
    };

    void bootstrap();

    return () => {
      isActive = false;
    };
  }, [model, useSessionApi]);

  useEffect(() => {
    if (model) {
      model.locale = currentLocale;
      Object.assign(model, translations[currentLocale]);
    }
  }, [currentLocale, model]);

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const newLocale = e.target.value as Language;
    setCurrentLocale(newLocale);
  };

  useEffect(() => {
    if (model) {
      const headerElement = document.querySelector('header');
      if (headerElement && isMobileViewport) {
        const headerHeight = headerElement.offsetHeight;
        setMobilePaddingTop(`${headerHeight}px`);
        setHeaderPosition('fixed');
      }
    }
  }, [isMobileViewport, model, showSurvey]);

  const handleCountryChange = (value: string): void => {
    const countryCode = normalizeCountryCode(value);
    setSelectedCountry(countryCode);
    const savedState = getSavedState() || { data: {}, currentPageNo: 0 };
    savedState.country = countryCode;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(savedState));
  };

  const handleStartSurvey = (): void => {
    clearEmailOptInSentFlag();
    setSubmitStatus(null);
    setFlowMessage(null);

    if (useSessionApi && model) {
      void startBackendSession(model);
      return;
    }

    setShowSurvey(true);
  };

  const handleRestart = (): void => {
    if (model) {
      model.clear();
      clearSurveyState();
      previousPageRef.current = 0;
    }

    setShowSurvey(false);
    setShowThankYou(false);
    setSubmitStatus(null);
    setFlowMessage(null);
    setProgress(0);

    if (useSessionApi) {
      setAppPhase('readyToStart');
      setExpectedStepIds([]);
      setReceivedStepIds([]);
      setTurnstileToken('');
      if (TURNSTILE_CONFIGURED) {
        setTurnstileResetNonce((value) => value + 1);
      }
    }
  };

  const handleTurnstileTokenChange = useCallback((token: string): void => {
    setTurnstileToken(token);

    if (token) {
      setFlowMessageKey((current) => {
        if (current === 'turnstileRequired' || current === 'turnstileUnavailable') {
          return null;
        }

        return current;
      });
    }
  }, []);

  const handleTurnstileError = useCallback((errorCode?: string): void => {
    if (errorCode) {
      console.error(`[Turnstile] widget error code: ${errorCode}`);
    }

    setTurnstileToken('');
    setAppPhase('readyToStart');
    setFlowMessage('turnstileUnavailable');
  }, [setFlowMessage]);

  const handleShowLegals = (): void => {
    scrollViewportToTop();
    setShowAboutUs(false);
    setShowLegals(true);
  };

  const handleBackToSurvey = (): void => {
    setShowLegals(false);
    setShowAboutUs(false);
  };

  const handleShowAboutUs = (): void => {
    scrollViewportToTop();
    setShowLegals(false);
    setShowAboutUs(true);
  };

  const handleEmailSubmit = async (email: string): Promise<void> => {
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      if (useSessionApiRef.current) {
        await withCsrfRetry('subscribeEmail', () => subscribeEmail({ email }));
      } else {
        const surveyData = {
          ...(model?.data as Record<string, unknown>),
          country: selectedCountry,
        };

        const response = await fetch('/api/survey/submit', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            surveyData,
            locale: currentLocale,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to submit');
        }
      }

      setEmailOptInSent(true);
      setEmailAlreadySent(true);
      setSubmitStatus('success');
    } catch (error) {
      const err = error as { status?: number };
      if (useSessionApiRef.current && err?.status === 409) {
        setEmailOptInSent(true);
        setEmailAlreadySent(true);
        setSubmitStatus('success');
        return;
      }

      if (err?.status) {
        applyApiErrorState(err.status);
      } else {
        console.error('Error submitting email:', error);
      }

      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!model) return null;

  const useCompactHeader = isMobileViewport && isHeaderCompact;

  const flowMessage = flowMessageKey && !isFlowMessageDismissed
    ? {
      text: statusMessages[currentLocale][flowMessageKey],
      severity: mapMessageSeverity(flowMessageKey),
    }
    : null;

  return (
    <div className="survey-container" style={{ paddingTop: mobilePaddingTop }}>
      <HeaderComponent
        language={currentLocale}
        onLanguageChange={handleLanguageChange}
        progress={showSurvey && !showThankYou && !showLegals && !showAboutUs ? progress : null}
        isCompact={useCompactHeader}
        position={headerPosition}
      />
      {flowMessage && (
        <div className={`api-status-banner api-status-banner--${appPhase}`}>
          <StatusAlert
            severity={flowMessage.severity}
            message={flowMessage.text}
            closeLabel={statusMessages[currentLocale].closeMessage}
            onClose={() => setIsFlowMessageDismissed(true)}
          />
        </div>
      )}
      {showLegals ? (
        <LegalsPage language={currentLocale} onBack={handleBackToSurvey} />
      ) : showAboutUs ? (
        <AboutUsPage language={currentLocale} onBack={handleBackToSurvey} />
      ) : !showSurvey ? (
        <WelcomeBlock
          language={currentLocale}
          onStart={handleStartSurvey}
          onLanguageChange={handleLanguageChange}
          onCountryChange={handleCountryChange}
          selectedCountry={selectedCountry}
          turnstileEnabled={TURNSTILE_CONFIGURED}
          turnstileSiteKey={TURNSTILE_SITE_KEY}
          turnstileToken={turnstileToken}
          turnstileResetNonce={turnstileResetNonce}
          onTurnstileTokenChange={handleTurnstileTokenChange}
          onTurnstileError={handleTurnstileError}
        />
      ) : showThankYou ? (
        <ThankYouBlock
          language={currentLocale}
          onSubmit={handleEmailSubmit}
          onRestart={handleRestart}
          isSubmitting={isSubmitting}
          submitStatus={submitStatus}
          completionMessage={isFinalizing ? statusMessages[currentLocale].completionPending : null}
          emailAlreadySent={emailAlreadySent}
        />
      ) : (
        <Survey ref={surveyRef} model={model} />
      )}
      <FooterComponent language={currentLocale} onLegalsClick={handleShowLegals} onAboutUsClick={handleShowAboutUs} />
    </div>
  );
}

export default SurveyComponent;
